import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { LEASE_VERSION } from './lease';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  type User as FbUser,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db, firebaseConfigured } from './firebase';
import type { UserDoc } from './types';
import { demoEnabled } from './demoFlag';

// Demo/preview access. On in local dev, and on the STAGE/demo deploy (built with
// VITE_DEMO=1). PRODUCTION (app.ajmaviation.com) never sets VITE_DEMO, so the
// preview picker never appears there — real pilots only see the real login. The old
// ?demo=1 localStorage opt-in was removed 2026-06-16 (it leaked the preview onto the
// live login once a browser had ever visited with the flag).
export { demoEnabled } from './demoFlag';

// 'cfi' is a demo-only instructor seat (billingProfile 'cfi', promo plan) used to walk the
// discovery-flight flow. Not on the picker; reached by setting the demo role directly.
type DemoRole = 'admin' | 'lessee_active' | 'lessee_incomplete' | 'lessee_pending' | 'cfi';

interface AuthState {
  ready: boolean;
  configured: boolean;
  fbUser: FbUser | null;
  profile: UserDoc | null;
  isAdmin: boolean;
  isActiveLessee: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUpLessee: (params: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone: string;
    intake?: Record<string, string>;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  demoMode: boolean;
  enterDemo: (role: DemoRole) => void;
  exitDemo: () => void;
  // Admin "View as user": render the app as a chosen lessee sees it (read-only in
  // practice — Firestore rules still evaluate the real admin, so cross-user writes
  // are denied). `viewAs(uid)` enters, `viewAs(null)` exits back to admin.
  isViewingAs: boolean;
  viewAsName: string | null;
  viewAs: (uid: string | null) => void;
}

// Rich sample data so the preview shows a fully-populated app + a pre-filled
// onboarding wizard (no typing needed to walk through it).
const DEMO_DETAILS = {
  personal: {
    firstName: 'Demo',
    lastName: 'Lessee',
    dateOfBirth: '1986-04-12',
    address: '742 Spring St, Long Beach, CA 90802',
    phone: '562-555-0147',
    emergencyContact: { name: 'Jordan Lessee', relationship: 'Spouse', phone: '562-555-0188' },
  },
  pilot: {
    certificateType: 'Private Pilot',
    certificateNumber: '3812457',
    certificateIssueDate: '2014-08-20',
    ratings: ['Airplane Single-Engine Land', 'Instrument Airplane'],
    medicalClass: '3rd' as const,
    medicalIssueDate: '2025-03-15',
    medicalExpiration: '2027-03-31',
    medicalLimitations: '',
    lastBFR: '2025-05-01',
    bfrExpiration: '2027-05-31',
    lastIPC: '2025-02-10',
    ipcExpiration: '2025-08-31',
    totalHours: 640,
    hoursIn172: 180,
    hoursLast90: 14,
    hoursLast12Months: 70,
  },
  disclosures: {
    priorAccident: { answer: 'no' as const, explanation: '' },
    priorEnforcement: { answer: 'no' as const, explanation: '' },
    priorInsuranceRefusal: { answer: 'no' as const, explanation: '' },
    dui: { answer: 'no' as const, explanation: '' },
    pendingActions: { answer: 'no' as const, explanation: '' },
  },
  insurance: {
    carrier: 'Avemco',
    policyNumber: 'AV-7741922',
    liabilityLimit: 1000000,
    expirationDate: '2027-01-31',
  },
};

function demoProfile(role: DemoRole): UserDoc {
  const base = {
    uid: `demo-${role}`,
    email: 'demo@ajmaviation.com',
    createdAt: { toDate: () => new Date() } as never,
  };
  if (role === 'admin') {
    return { ...base, role: 'admin', status: 'active', personal: demoEnabled() ? { firstName: 'Jordan', lastName: 'Wells' } : { firstName: 'Casey', lastName: 'Casburn' } } as UserDoc;
  }
  if (role === 'cfi') {
    return {
      ...base,
      role: 'lessee',
      status: 'active',
      billingProfile: 'cfi',
      plan: 'promo',
      ...DEMO_DETAILS,
      personal: { ...DEMO_DETAILS.personal, firstName: 'Taylor', lastName: 'Brooks' },
      pilot: { ...DEMO_DETAILS.pilot, certificateType: 'Commercial Pilot', ratings: ['Airplane Single-Engine Land', 'Instrument Airplane', 'Flight Instructor (CFI)'] },
      leaseSignedAt: { toDate: () => new Date('2026-07-01T12:00:00') } as never,
      leaseSignedVersion: LEASE_VERSION,
    } as UserDoc;
  }
  const status =
    role === 'lessee_active' ? 'active' : role === 'lessee_incomplete' ? 'profile_incomplete' : 'pending_approval';
  // The active demo pilot is fully onboarded — signed lease included — so the preview
  // shows the working product rather than the booking gate (lib/bookingRules.ts).
  const leaseSigned =
    role === 'lessee_active'
      ? { leaseSignedAt: { toDate: () => new Date('2026-07-01T12:00:00') } as never, leaseSignedVersion: LEASE_VERSION }
      : {};
  return { ...base, role: 'lessee', status, ...DEMO_DETAILS, ...leaseSigned } as UserDoc;
}

const Ctx = createContext<AuthState | null>(null);

const DEMO_KEY = 'ajm-portal-demo-role';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [fbUser, setFbUser] = useState<FbUser | null>(null);
  const [profile, setProfile] = useState<UserDoc | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [demoRole, setDemoRole] = useState<DemoRole | null>(() => {
    if (!demoEnabled()) return null;
    const v = typeof window !== 'undefined' ? window.localStorage.getItem(DEMO_KEY) : null;
    return v as DemoRole | null;
  });
  // Admin "View as user" target.
  const [viewAsUid, setViewAsUid] = useState<string | null>(null);
  const [viewAsProfile, setViewAsProfile] = useState<UserDoc | null>(null);

  useEffect(() => {
    if (!firebaseConfigured || !auth) {
      setAuthReady(true);
      setProfileReady(true);
      return;
    }
    const unsub = onAuthStateChanged(auth, (u) => {
      setFbUser(u);
      setAuthReady(true);
      if (!u) {
        setProfile(null);
        setProfileReady(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!fbUser || !db) {
      setProfile(null);
      return;
    }
    setProfileReady(false);
    const ref = doc(db, 'users', fbUser.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setProfile(snap.exists() ? (snap.data() as UserDoc) : null);
        setProfileReady(true);
      },
      () => setProfileReady(true)
    );
    return () => unsub();
  }, [fbUser]);

  // Load the impersonated lessee's profile (reads run under the real admin token).
  useEffect(() => {
    if (!viewAsUid || !db) {
      setViewAsProfile(null);
      return;
    }
    const unsub = onSnapshot(doc(db, 'users', viewAsUid), (snap) =>
      setViewAsProfile(snap.exists() ? (snap.data() as UserDoc) : null)
    );
    return () => unsub();
  }, [viewAsUid]);

  const value = useMemo<AuthState>(() => {
    const inDemo = Boolean(demoRole) && demoEnabled();
    // Only a real admin may impersonate, and only once the target profile has loaded.
    const viewingAs = !inDemo && Boolean(viewAsUid) && profile?.role === 'admin' && Boolean(viewAsProfile);
    const effProfile = inDemo ? demoProfile(demoRole as DemoRole) : viewingAs ? viewAsProfile : profile;
    const effFbUser = inDemo
      ? ({ uid: `demo-${demoRole}`, email: 'demo@ajmaviation.com' } as FbUser)
      : viewingAs
        ? ({ uid: viewAsUid as string, email: viewAsProfile?.email } as FbUser)
        : fbUser;
    const isAdmin = effProfile?.role === 'admin';
    const isActiveLessee = effProfile?.role === 'lessee' && effProfile?.status === 'active';
    const viewAsName = viewingAs
      ? `${viewAsProfile?.personal?.firstName ?? ''} ${viewAsProfile?.personal?.lastName ?? ''}`.trim() ||
        viewAsProfile?.email ||
        'lessee'
      : null;

    return {
      ready: inDemo || (authReady && profileReady),
      configured: firebaseConfigured,
      fbUser: effFbUser,
      profile: effProfile,
      isAdmin,
      isActiveLessee,
      demoMode: inDemo,
      enterDemo(role: DemoRole) {
        if (!demoEnabled()) return;
        setDemoRole(role);
        window.localStorage.setItem(DEMO_KEY, role);
      },
      exitDemo() {
        setDemoRole(null);
        window.localStorage.removeItem(DEMO_KEY);
      },
      isViewingAs: viewingAs,
      viewAsName,
      viewAs(uid: string | null) {
        setViewAsUid(uid);
      },

      async signIn(email, password) {
        if (!auth) throw new Error('Firebase not configured. See SETUP.md.');
        await signInWithEmailAndPassword(auth, email, password);
      },

      async signInWithGoogle() {
        if (!auth || !db) throw new Error('Firebase not configured. See SETUP.md.');
        const provider = new GoogleAuthProvider();
        const cred = await signInWithPopup(auth, provider);
        const u = cred.user;
        // First Google sign-in: create the lessee profile and send them straight
        // into onboarding (same as email registration — PIN is the only gate).
        const ref = doc(db, 'users', u.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          const parts = (u.displayName ?? '').trim().split(/\s+/);
          await setDoc(ref, {
            uid: u.uid,
            email: u.email,
            role: 'lessee',
            // PIN-gated registration is enough to start; the real vetting is the
            // document review after onboarding (verification_pending → active).
            status: 'profile_incomplete',
            createdAt: serverTimestamp(),
            personal: {
              firstName: parts[0] ?? '',
              lastName: parts.slice(1).join(' '),
              phone: u.phoneNumber ?? '',
            },
          });
        }
      },

      async signUpLessee({ email, password, firstName, lastName, phone, intake }) {
        if (!auth || !db) throw new Error('Firebase not configured. See SETUP.md.');
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        const ref = doc(db, 'users', cred.user.uid);
        await setDoc(ref, {
          uid: cred.user.uid,
          email,
          role: 'lessee',
          // Open signup (no PIN since 11/3/26); documents + insurance + checkout are the gate.
          status: 'profile_incomplete',
          createdAt: serverTimestamp(),
          personal: { firstName, lastName, phone },
          // What they told the lgb61.com popup (renting / time building / student, cert, hours, insurance).
          ...(intake && Object.keys(intake).length ? { intake } : {}),
        });
      },

      async signOut() {
        // While impersonating, the "sign out" control just exits back to admin.
        if (viewingAs) {
          setViewAsUid(null);
          return;
        }
        if (inDemo) {
          setDemoRole(null);
          window.localStorage.removeItem(DEMO_KEY);
          return;
        }
        if (!auth) return;
        await fbSignOut(auth);
      },

      async resetPassword(email) {
        if (!auth) throw new Error('Firebase not configured. See SETUP.md.');
        await sendPasswordResetEmail(auth, email);
      },
    };
  }, [fbUser, profile, authReady, profileReady, demoRole, viewAsUid, viewAsProfile]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
